import React, { useState, useEffect, useRef, useCallback } from "react";
import classes from "./style.module.scss";
import { useAppHeader } from "~/app/modules/appHeader";
import usePageTitle from "~/app/hooks/usePageTitle";
import { route } from "~/app/helpers/route";
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import { useStoreActions } from "~/store";
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import Icon from "~/app/common/Icon";
import useTranslation from '~/app/hooks/useTranslation';

const ActiveTasks: React.FC = () => {
  const { t } = useTranslation(); // Add translation hook
  const { setData, clearData } = useAppHeader();
  
  const getActiveEntry = useStoreActions((actions: any) => actions.timeManagement.getActiveEntry);
  const pauseResumeTimer = useStoreActions((actions: any) => actions.timeManagement.pauseResumeTimer);

  const [isLoading, setIsLoading] = useState(true);
  const [allEntries, setAllEntries] = useState<any[]>([]);
  const [pausingEntryId, setPausingEntryId] = useState<number | null>(null);
  
  const allEntriesRef = useRef<any[]>([]);

  usePageTitle(t('timeManagement.activeTasks')); // Translated page title

  useEffect(() => {
    setData({
      title: t('timeManagement.activeTasks'),
      subTitle: t('timeManagement.oneTask'),
      backLink: route(TIME_MANAGEMENT_ROUTES.activeTime),
      isTimeManagementPage: false,
      showUser: false,
      machineId: '',
      projectId: '',
      parentFolderId: 0,
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      isAddTimeForm: false,
      openInFull: true
    });

    return () => clearData();
  }, [setData, clearData, t]);

  const fetchActiveEntry = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const response = await getActiveEntry();      
      if (response?.data) {
        const entries = Array.isArray(response.data) ? response.data : (response.data.entries || []);
        
        if (!Array.isArray(entries) || entries.length === 0) {
          setAllEntries([]);
          setIsLoading(false);
          return;
        }
        
        setAllEntries(entries);
        allEntriesRef.current = entries;
        
      }
    } catch (error) {
      console.error('Error fetching active entry:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getActiveEntry]);
  
  useEffect(() => {
    fetchActiveEntry();
  }, [fetchActiveEntry]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    
    const updateElapsedTime = () => {
      const entries = allEntriesRef.current;
      if (!entries || entries.length === 0) return;
      
      const updatedEntries = entries.map((entry: any) => {
        let entryTotal = 0;
        const isEntryActive = entry.is_active === true;
        
        if (entry.time_sessions && entry.time_sessions.length > 0) {
          entry.time_sessions.forEach((session: any) => {
            if (session.start_time) {
              const startParts = session.start_time.split(':');
              const startHours = parseInt(startParts[0]) || 0;
              const startMins = parseInt(startParts[1]) || 0;
              const startSecs = parseInt(startParts[2]) || 0;
              const sessionStartMinutes = startHours * 60 + startMins + (startSecs / 60);
              
              let sessionEndMinutes;
              
              if (session.end_time) {
                const endParts = session.end_time.split(':');
                const endHours = parseInt(endParts[0]) || 0;
                const endMins = parseInt(endParts[1]) || 0;
                const endSecs = parseInt(endParts[2]) || 0;
                sessionEndMinutes = endHours * 60 + endMins + (endSecs / 60);
                
                const sessionDuration = sessionEndMinutes - sessionStartMinutes;
                if (sessionDuration > 0) {
                  entryTotal += sessionDuration;
                }
              } else if (session.is_active && isEntryActive) {
                const now = new Date();
                sessionEndMinutes = now.getHours() * 60 + now.getMinutes() + (now.getSeconds() / 60);
                
                const sessionDuration = sessionEndMinutes - sessionStartMinutes;
                if (sessionDuration > 0) {
                  entryTotal += sessionDuration;
                }
              }
            }
          });
        }
        
        return {
          ...entry,
          total_minutes: Math.round(entryTotal)
        };
      });
      
      setAllEntries(updatedEntries);
      allEntriesRef.current = updatedEntries;
    };
    
    updateElapsedTime();
    const timer = setInterval(updateElapsedTime, 1000); 
    return () => clearInterval(timer);
  }, [isLoading]);

  const formatTime = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const handlePauseToggle = async (entry: any) => {
    const timeManagementId = entry.id;
    const isEntryActive = entry.is_active === true;
    
    if (!timeManagementId) {
      return;
    }

    setPausingEntryId(timeManagementId);

    try {
      const action = isEntryActive ? 'pause' : 'resume';      
      await pauseResumeTimer({ 
        id: timeManagementId, 
        action: action 
      });
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchActiveEntry(); 
    } catch (error: any) {
      console.error('Error:', error);
      
      const errorString = String(error);
      const isSuccess = errorString.includes('resumed') || 
                      errorString.includes('paused') ||
                      errorString.includes('Timer resumed') ||
                      errorString.includes('Timer paused');
      
      if (!isSuccess) {
        console.error(t('timeManagement.failedToUpdateTimer'));
      } else {
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchActiveEntry();
      }
    } finally {
      setPausingEntryId(null);
    }
  };

  if (isLoading) {
    return (
      <div className={classes.activeTasksWrapper}>
        <div className={classes.content}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {t('timeManagement.loading')}
          </div>
        </div>
      </div>
    );
  }

  if (allEntries.length === 0) {
    return (
      <div className={classes.activeTasksWrapper}>
        <div className={classes.content}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {t('timeManagement.noActiveTasks')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.activeTasksWrapper}>
      <div className={classes.content}>
        {allEntries.map((entry: any) => {
          const isEntryActive = entry.is_active === true;
          const isPausing = pausingEntryId === entry.id;
          
          return (
            <div key={entry.id} className={`${classes.taskCard} ${isEntryActive ? classes.activeCard : classes.pausedCard}`}>
              <div className={classes.taskHeader}>
                <div className={classes.taskInfo}>
                  <div className={classes.projectNumber}>
                    {entry.project?.number || 'N/A'} • {t('timeManagement.project')} {entry.project?.number || 'N/A'}
                  </div>
                  <div className={classes.taskTitle}>
                    {entry.mode_type === 0 && entry.machine ? `${t('timeManagement.machine')}: ${entry.machine.intern}` :
                     entry.mode_type === 1 && entry.activity ? t('timeManagement.task') :
                     entry.mode_type === 2 ? t('timeManagement.unspecified') : t('timeManagement.task')}
                  </div>
                  {entry.activity && (entry.mode_type === 0 || entry.mode_type === 1) && (
                    <div className={classes.taskSubtitle}>{entry.activity.name}</div>
                  )}
                </div>
              </div>

              <div className={classes.taskBody}>
                <div className={classes.timeDisplay}>
                  {formatTime(entry.total_minutes || 0)}
                </div>
                <button 
                  className={`${classes.pauseButton} ${isEntryActive ? classes.pauseActive : classes.pauseInactive}`}
                  onClick={() => handlePauseToggle(entry)}
                  disabled={isPausing}
                >
                  {isPausing ? (
                    <Icon name={'hourglass_empty' as Icons} className={classes.pauseIcon} />
                  ) : isEntryActive ? (
                    <Icon name={'pause_circle' as Icons} className={classes.pauseIcon} />
                  ) : (
                    <Icon name={'play_arrow' as Icons} className={classes.pauseIcon} />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveTasks;